import React from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import Img from "gatsby-image"
import { useIntl } from "gatsby-plugin-intl"
import { FiArrowRight, FiCode, FiCpu, FiShield, FiCloud } from "react-icons/fi"
import ButtonLink from "../components/ButtonLink"
import Link from "../components/Link"
import PageMetadata from "../components/PageMetadata"
import { translateMessageId } from "../utils/translations"

const Page = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.text};
  h1,
  h2,
  h3 {
    font-family: inherit;
    font-style: normal;
  }
  h2 {
    font-size: clamp(1.8rem, 3vw, 2.6rem);
    line-height: 1.15;
    letter-spacing: -0.035em;
    margin: 0 0 1.25rem;
  }
  h3 {
    font-size: 1.25rem;
    line-height: 1.35;
    margin: 0 0 1rem;
  }
  a:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 5px;
  }
`
const Container = styled.div`
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 0 2.5rem;
  @media (max-width: 600px) {
    padding: 0 1.25rem;
  }
`
const Eyebrow = styled.p`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin: 0 0 1.25rem;
`
const Hero = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  width: 100%;
  background: #08121c;
  color: #fff;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(
        90deg,
        rgba(4, 12, 21, 0.8) 0%,
        rgba(4, 12, 21, 0.62) 42%,
        rgba(4, 12, 21, 0.08) 80%
      ),
      linear-gradient(0deg, rgba(4, 12, 21, 0.45), transparent 45%);
    pointer-events: none;
  }
  a:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 700px) {
    &::after {
      background: linear-gradient(
        90deg,
        rgba(4, 12, 21, 0.9),
        rgba(4, 12, 21, 0.64)
      );
    }
  }
`
const HeroImage = styled(Img)`
  position: absolute !important;
  inset: 0;
  z-index: -2;
  width: 100%;
  height: 100%;
`
const HeroContent = styled(Container)`
  min-height: 610px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-top: 4.5rem;
  padding-bottom: 4.5rem;
  h1 {
    max-width: 760px;
    color: #fff;
    font-size: clamp(2.75rem, 5.5vw, 4.75rem);
    font-weight: 500;
    line-height: 1.06;
    letter-spacing: -0.045em;
    margin: 0 0 1.75rem;
    text-wrap: balance;
  }
  > p {
    max-width: 560px;
    color: #e0e6ec;
    font-size: 1.125rem;
    line-height: 1.7;
    margin: 0 0 2rem;
  }
  @media (max-width: 700px) {
    min-height: 600px;
    padding-top: 3rem;
    padding-bottom: 3rem;
    h1 {
      font-size: clamp(2.5rem, 8.8vw, 3.75rem);
    }
    > p {
      font-size: 1rem;
    }
  }
`
const HeroKicker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  margin-bottom: 1.75rem;
  &::before {
    content: "";
    width: 2rem;
    height: 2px;
    flex-shrink: 0;
    background: #6d9dff;
  }
`
const HeroActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  @media (max-width: 400px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 1.4rem;
  }
`
const HeroPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 1.2rem;
  background: #fff;
  color: #102030 !important;
  padding: 1rem 1.25rem;
  border: 1px solid #fff;
  border-radius: 0;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    background: #dce7ff;
    border-color: #dce7ff;
  }
  svg {
    flex-shrink: 0;
  }
`
const HeroSecondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.55);
  color: #fff !important;
  font-size: 0.95rem;
  text-decoration: none;
  &:hover {
    border-color: #fff;
  }
`
const Lead = styled.p`
  max-width: 62ch;
  font-size: 1.125rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text200};
  margin: 0 0 1.75rem;
`
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  a {
    padding: 0.8rem 1.1rem;
    border-radius: 6px;
    white-space: normal;
    font-weight: 600;
  }
  @media (max-width: 400px) {
    flex-direction: column;
  }
`
const Note = styled.p`
  margin: 1.25rem 0 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text200};
`
const ServiceRail = styled.nav`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  ${Container} {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  @media (max-width: 800px) {
    ${Container} {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  @media (max-width: 420px) {
    ${Container} {
      grid-template-columns: 1fr;
    }
  }
`
const ServiceRailLink = styled(Link)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 0.7rem;
  padding: 1.5rem;
  border-left: 1px solid ${({ theme }) => theme.colors.lightBorder};
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text};
  &:last-child {
    border-right: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  > span {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.7rem;
    letter-spacing: 0.06em;
  }
  > strong {
    display: flex;
    gap: 0.75rem;
    justify-content: space-between;
    align-items: center;
    font-weight: 500;
    font-size: 0.95rem;
  }
  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
  }
  &:hover {
    background: ${({ theme }) => theme.colors.background};
  }
  @media (max-width: 800px) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  @media (max-width: 420px) {
    padding: 1rem 0;
    border-left: 0;
    &:last-child {
      border-right: 0;
      border-bottom: 0;
    }
  }
`
const Section = styled.section`
  padding: 5rem 0;
  scroll-margin-top: 6rem;
  @media (max-width: 600px) {
    padding: 3rem 0;
  }
`
const SoftSection = styled(Section)`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
`
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    ${({ $columns }) => $columns || 2},
    minmax(0, 1fr)
  );
  gap: 1.25rem;
  margin-top: 2.5rem;
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`
const Card = styled.article`
  display: flex;
  flex-direction: column;
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.background};
  p {
    color: ${({ theme }) => theme.colors.text200};
    line-height: 1.65;
  }
  > svg {
    color: ${({ theme }) => theme.colors.primary};
    width: 28px;
    height: 28px;
    margin-bottom: 1.5rem;
  }
  @media (max-width: 600px) {
    padding: 1.5rem;
  }
`
const TextLink = styled(Link)`
  display: inline-flex;
  gap: 0.75rem;
  align-items: center;
  font-weight: 600;
  text-decoration: none;
  margin-top: auto;
  font-size: 0.95rem;
  svg {
    flex-shrink: 0;
  }
  &:hover {
    text-decoration: underline;
  }
`
const ExtraServices = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.75rem 1.5rem;
  padding-top: 2rem;
  font-size: 0.875rem;
  span {
    color: ${({ theme }) => theme.colors.text200};
  }
`
const SectionHeader = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: space-between;
  align-items: flex-end;
  ${Lead} {
    margin-bottom: 0;
  }
  > a {
    flex-shrink: 0;
  }
  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
  }
`
const Category = styled.p`
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 1rem;
`
const Step = styled.article`
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  padding-top: 1.5rem;
  span {
    display: block;
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 1.5rem;
    font-size: 0.875rem;
    font-weight: 700;
  }
  h3 {
    font-size: 1.125rem;
  }
  p {
    color: ${({ theme }) => theme.colors.text200};
    line-height: 1.65;
    font-size: 0.95rem;
    margin-bottom: 0;
  }
`
const About = styled(Container)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`
const Contact = styled(Section)`
  text-align: center;
  ${Lead} {
    margin-left: auto;
    margin-right: auto;
  }
  ${Actions} {
    justify-content: center;
  }
`

const HomePage = ({ data }) => {
  const intl = useIntl()
  const t = (key) => translateMessageId(`home-${key}`, intl)
  const email = `mailto:gerencia@newinntech.com?subject=${encodeURIComponent(
    t("email-subject")
  )}&body=${encodeURIComponent(t("email-body"))}`
  const solutions = [
    { key: "software", icon: FiCode, to: "/desarrollo/" },
    { key: "ai", icon: FiCpu, to: "/ai-service/" },
    { key: "cloud", icon: FiCloud, to: "/arquitectura/detalle/" },
    { key: "devsecops", icon: FiShield, to: "/devsecops/" },
  ]
  const products = [
    { key: "devops", name: "DevOps Monitor", to: "/productos/devops-monitor/" },
    { key: "pmo", name: "PMO Manager", to: "/productos/pmo-manager/" },
    { key: "ai", name: "Gencode AI", to: "/productos/gencode-ai/" },
  ]
  return (
    <Page>
      <PageMetadata
        title={t("meta-title")}
        description={t("meta-description")}
      />
      <Hero>
        <HeroImage
          fluid={{
            ...data.corporateHero.childImageSharp.fluid,
            sizes: "(max-width: 700px) 1300px, 100vw",
          }}
          alt=""
          loading="eager"
          fadeIn={false}
          imgStyle={{ objectPosition: "65% center" }}
        />
        <HeroContent>
          <HeroKicker>{t("eyebrow")}</HeroKicker>
          <h1>{t("headline")}</h1>
          <p>{t("intro")}</p>
          <HeroActions>
            <HeroPrimary to="#contacto">
              {t("contact-cta")}
              <FiArrowRight aria-hidden="true" />
            </HeroPrimary>
            <HeroSecondary to="#soluciones">
              {t("solutions-cta")}
              <FiArrowRight aria-hidden="true" />
            </HeroSecondary>
          </HeroActions>
        </HeroContent>
      </Hero>
      <ServiceRail aria-label={t("capabilities-label")}>
        <Container>
          {solutions.map(({ key, to }, index) => (
            <ServiceRailLink key={key} to={to}>
              <span aria-hidden="true">0{index + 1}</span>
              <strong>
                {t(`${key}-title`)}
                <FiArrowRight aria-hidden="true" />
              </strong>
            </ServiceRailLink>
          ))}
        </Container>
      </ServiceRail>
      <Section id="soluciones" aria-labelledby="solutions-title">
        <Container>
          <Eyebrow>{t("solutions-label")}</Eyebrow>
          <h2 id="solutions-title">{t("solutions-title")}</h2>
          <Lead>{t("solutions-intro")}</Lead>
          <Grid>
            {solutions.map(({ key, icon: Icon, to }) => (
              <Card key={key}>
                <Icon aria-hidden="true" />
                <h3>{t(`${key}-title`)}</h3>
                <p>{t(`${key}-desc`)}</p>
                <TextLink to={to}>
                  {t(`${key}-link`)}
                  <FiArrowRight aria-hidden="true" />
                </TextLink>
              </Card>
            ))}
          </Grid>
          <ExtraServices>
            <span>{t("other-services")}</span>
            {[
              ["consulting", "/consultoria/"],
              ["education", "/capacitacion/"],
              ["blockchain", "/blockchain2/"],
              ["iot", "/iot/"],
            ].map(([key, to]) => (
              <Link key={key} to={to}>
                {t(key)}
              </Link>
            ))}
          </ExtraServices>
        </Container>
      </Section>
      <SoftSection aria-labelledby="products-title">
        <Container>
          <SectionHeader>
            <div>
              <Eyebrow>{t("products-label")}</Eyebrow>
              <h2 id="products-title">{t("products-title")}</h2>
              <Lead>{t("products-intro")}</Lead>
            </div>
            <TextLink to="/productos/">
              {t("catalog-cta")}
              <FiArrowRight aria-hidden="true" />
            </TextLink>
          </SectionHeader>
          <Grid $columns={3}>
            {products.map(({ key, name, to }) => (
              <Card key={key}>
                <Category>{t(`product-${key}-category`)}</Category>
                <h3>{name}</h3>
                <p>{t(`product-${key}-desc`)}</p>
                <TextLink to={to} ariaLabel={`${t("product-link")}: ${name}`}>
                  {t("product-link")}
                  <FiArrowRight aria-hidden="true" />
                </TextLink>
              </Card>
            ))}
          </Grid>
        </Container>
      </SoftSection>
      <Section aria-labelledby="method-title">
        <Container>
          <Eyebrow>{t("method-label")}</Eyebrow>
          <h2 id="method-title">{t("method-title")}</h2>
          <Lead>{t("method-intro")}</Lead>
          <Grid $columns={4}>
            {[1, 2, 3, 4].map((step) => (
              <Step key={step}>
                <span aria-hidden="true">0{step}</span>
                <h3>{t(`step-${step}-title`)}</h3>
                <p>{t(`step-${step}-desc`)}</p>
              </Step>
            ))}
          </Grid>
        </Container>
      </Section>
      <SoftSection>
        <About>
          <div>
            <Eyebrow>{t("about-label")}</Eyebrow>
            <h2>{t("about-title")}</h2>
          </div>
          <div>
            <Lead>{t("about-desc")}</Lead>
            <TextLink to="/info/quienes-somos/">
              {t("about-cta")}
              <FiArrowRight aria-hidden="true" />
            </TextLink>
          </div>
        </About>
      </SoftSection>
      <Contact id="contacto" aria-labelledby="contact-title">
        <Container>
          <Eyebrow>{t("contact-label")}</Eyebrow>
          <h2 id="contact-title">{t("contact-title")}</h2>
          <Lead>{t("contact-desc")}</Lead>
          <Actions>
            <ButtonLink to={email}>{t("email-cta")}</ButtonLink>
          </Actions>
          <Note>
            {t("email-note")}
            <br />
            <Link to="mailto:gerencia@newinntech.com">
              gerencia@newinntech.com
            </Link>
          </Note>
        </Container>
      </Contact>
    </Page>
  )
}
export default HomePage

export const query = graphql`
  query {
    corporateHero: file(
      relativePath: { eq: "home/corporate-architecture-v1.png" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 85) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`
